// SaaS-layer mock data: orgs, plans, audit, announcements, flags, portal-specific.
import { clients, shoots, galleries, invoices, contracts } from "./data";

export type OrgStatus = "active" | "trialing" | "suspended";
export type PlanId = "solo" | "studio" | "agency";

export interface Plan {
  id: PlanId;
  name: string;
  priceCents: number;
  interval: "month" | "year";
  limits: { seats: number; storageGb: number; shootsPerMonth: number };
  features: { clientPortal: boolean; eSign: boolean; aiCulling: boolean; whiteLabel: boolean };
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  planId: PlanId;
  status: OrgStatus;
  ownerUserId: string;
  seats: number;
  storageUsedGb: number;
  mrrCents: number;
  lifetimeRevenueCents: number;
  createdAt: string;
  trialEndsAt?: string;
  brandHue: number;
  logoLetter: string;
}

export interface AuditEvent {
  id: string;
  at: string; // ISO
  actor: string;
  actorRole: string;
  orgId: string | null;
  action: string;
  target: string;
  ip: string;
}

export interface Announcement {
  id: string;
  audience: "all" | { planId: PlanId } | { orgId: string };
  message: string;
  severity: "info" | "warning" | "critical";
  startsAt: string;
  endsAt?: string;
}

export interface FeatureFlag {
  key: string;
  description: string;
  defaultValue: boolean;
}

export const plans: Plan[] = [
  {
    id: "solo",
    name: "Solo",
    priceCents: 2900,
    interval: "month",
    limits: { seats: 1, storageGb: 50, shootsPerMonth: 10 },
    features: { clientPortal: true, eSign: false, aiCulling: false, whiteLabel: false },
  },
  {
    id: "studio",
    name: "Studio",
    priceCents: 8900,
    interval: "month",
    limits: { seats: 5, storageGb: 250, shootsPerMonth: 50 },
    features: { clientPortal: true, eSign: true, aiCulling: true, whiteLabel: false },
  },
  {
    id: "agency",
    name: "Agency",
    priceCents: 19900,
    interval: "month",
    limits: { seats: 25, storageGb: 1000, shootsPerMonth: 500 },
    features: { clientPortal: true, eSign: true, aiCulling: true, whiteLabel: true },
  },
];

export const organizations: Organization[] = [
  { id: "org_1", name: "Thorne Studio", slug: "thorne", planId: "studio", status: "active", ownerUserId: "u_owner", seats: 4, storageUsedGb: 142, mrrCents: 8900, lifetimeRevenueCents: 312_00_00, createdAt: "2023-04-12", brandHue: 30, logoLetter: "T" },
  { id: "org_2", name: "Lumen & Co", slug: "lumenco", planId: "agency", status: "active", ownerUserId: "u_owner2", seats: 18, storageUsedGb: 612, mrrCents: 19900, lifetimeRevenueCents: 884_00_00, createdAt: "2022-09-01", brandHue: 120, logoLetter: "L" },
  { id: "org_3", name: "Northlight", slug: "northlight", planId: "solo", status: "trialing", ownerUserId: "u_owner3", seats: 1, storageUsedGb: 12, mrrCents: 0, lifetimeRevenueCents: 0, createdAt: "2025-05-22", trialEndsAt: "2026-06-21", brandHue: 200, logoLetter: "N" },
  { id: "org_4", name: "Verdant Studio", slug: "verdant", planId: "studio", status: "suspended", ownerUserId: "u_owner4", seats: 3, storageUsedGb: 88, mrrCents: 0, lifetimeRevenueCents: 41_00_00, createdAt: "2024-01-15", brandHue: 90, logoLetter: "V" },
];

export const auditEvents: AuditEvent[] = Array.from({ length: 60 }, (_, i) => {
  const actions = [
    ["org.plan.changed", "Solo → Studio"],
    ["user.invited", "editor@thornestudio.test"],
    ["invoice.paid", "INV-0140"],
    ["contract.signed", "Arc'teryx SS25"],
    ["gallery.delivered", "Vogue Oct Issue"],
    ["org.suspended", "Verdant Studio"],
    ["superadmin.impersonate.start", "Thorne Studio"],
    ["user.password.reset", "owner@lumenco.test"],
    ["announcement.published", "Scheduled maintenance"],
    ["feature_flag.toggled", "ai_culling"],
  ];
  const [action, target] = actions[i % actions.length];
  const actors = ["admin@platform.test", "owner@thornestudio.test", "owner@lumenco.test", "system"];
  const days = Math.floor(i / 3);
  const d = new Date(2026, 5, Math.max(1, 5 - days % 28), 14 - (i % 12), (i * 7) % 60);
  return {
    id: `a${i + 1}`,
    at: d.toISOString(),
    actor: actors[i % actors.length],
    actorRole: i % 4 === 0 ? "superadmin" : "org_owner",
    orgId: i % 4 === 0 ? null : organizations[i % organizations.length].id,
    action,
    target,
    ip: `10.${(i * 7) % 250}.${(i * 13) % 250}.${(i * 3) % 250}`,
  };
});

export const announcements: Announcement[] = [
  { id: "an_1", audience: "all", message: "Scheduled maintenance on June 15, 02:00 UTC.", severity: "info", startsAt: "2026-06-10" },
  { id: "an_2", audience: { planId: "solo" }, message: "Upgrade to Studio for AI culling.", severity: "info", startsAt: "2026-06-01" },
];

export const featureFlags: FeatureFlag[] = [
  { key: "ai_culling", description: "AI-assisted photo culling", defaultValue: true },
  { key: "e_sign_v2", description: "New e-signature workflow", defaultValue: false },
  { key: "portal_v2", description: "Redesigned client portal", defaultValue: false },
  { key: "stripe_v2", description: "New Stripe Checkout integration", defaultValue: true },
];

export const orgFeatureFlags: Record<string, Record<string, boolean>> = {
  org_2: { e_sign_v2: true, portal_v2: true },
  org_1: { e_sign_v2: true },
};

// Portal-side mock state
export const gallerySelections: Record<string, string[]> = {
  g1: ["img_3", "img_7", "img_18"],
};

export const contractSignatures: Record<string, { name: string; at: string }> = {
  ct1: { name: "Marcus Halden", at: "2026-06-02T14:23:00Z" },
};

// Link clients to portal users (mutates in dev for demo)
export const clientPortalLinks: Record<string, string> = {
  c1: "u_client_jane",
  c2: "u_client_sia",
  c4: "u_client_vogue",
};

// Platform KPIs (superadmin)
export const platformKpis = {
  totalOrgs: organizations.length,
  activeOrgs30d: organizations.filter((o) => o.status === "active").length,
  mrrCents: organizations.reduce((s, o) => s + o.mrrCents, 0),
  arrCents: organizations.reduce((s, o) => s + o.mrrCents, 0) * 12,
  signups30d: 1,
  churn30d: 1,
  storageGb: organizations.reduce((s, o) => s + o.storageUsedGb, 0),
  aiCallsMonth: 12_430,
};

export const platformMrrSeries = [
  { m: "Jan", v: 18800 },
  { m: "Feb", v: 21700 },
  { m: "Mar", v: 24600 },
  { m: "Apr", v: 26500 },
  { m: "May", v: 28800 },
  { m: "Jun", v: 31700 },
];

export function fmtCents(c: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(c / 100);
}

// Helpers for portal scoping
export function clientForPortalUser(portalUserId: string) {
  const clientId = Object.entries(clientPortalLinks).find(([, uid]) => uid === portalUserId)?.[0];
  return clientId ? clients.find((c) => c.id === clientId) ?? null : null;
}

export function portalDataForClient(clientName: string) {
  return {
    shoots: shoots.filter((s) => s.client === clientName),
    galleries: galleries.filter((g) => g.client === clientName),
    invoices: invoices.filter((v) => v.client === clientName),
    contracts: contracts.filter((c) => c.client === clientName),
  };
}
